import React from 'react';

import { TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SortableElement, sortableHandle } from 'react-sortable-hoc';
import ArrowTooltipCss from '../../../common/ArrowTooltipCss';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DragIcon from '@mui/icons-material/DragIndicator';

import Node from '../../../common/Node';
import CommonStyles from '../../../common/CommonStyles';

const useStyles = makeStyles({
  boundValue: {
    zIndex: 9999,
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    border: '1px solid #FBFBFB',
    '&:hover': {
      backgroundColor: '#EEECF8',
    },
  },
  boundInputAndControls: {
    minWidth: '400px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    overflow: 'auto',
  },
  boundEditContainer: {
    width: '54px',
    display: 'flex',
    flexShrink: '0',
    margin: '5px',
  },

  boundValueEditIcon: {
    fontSize: '20px',
    cursor: 'pointer',
    marginRight: '10px',
    '&:hover': {
      color: '#4E4C4C',
    },
  },
  boundValueRemoveIcon: {
    color: '#FF6F6F',
    fontSize: '24px',
    cursor: 'pointer',
    marginLeft: '-1px',
    '&:hover': {
      color: '#CE1E0E',
    },
  },
  boundValueCheckIcon: {
    fontSize: '24px',
    cursor: 'pointer',
    marginRight: '6px',
    '&:hover': {
      color: '#4E4C4C',
    },
  },
  boundValueContainer: {
    display: 'flex',
    minHeight: '34px',
  },
  boundValueText: {
    color: '#4E4C4C',
    fontSize: '12px',
    overflow: 'auto',
    whiteSpace: 'nowrap',
    margin: '8.75px 5px',
  },
  boundValueInput: {
    width: '100%',
    border: 'none',
    height: '24px',
    color: '#4E4C4C',
    fontSize: '12px',
    padding: '4px 5px',
    margin: '5px 0px',
    background: '#EEECF8',
    boxSizing: 'border-box',
    '&:focus': {
      background: '#D9D8ED',
    },
  },
  textAreaClassName: {
    '&:focus': {
      background: '#D9D8ED',
    },
  },
  boundValueDragIconContainer: {
    width: '30px',
    height: '100%',
    display: 'flex',
    flexShrink: '0',
    marginTop: '10px',
    alignItems: 'top',
    justifyContent: 'center',
    '&:hover': {
      '& .drag-handle-icon': {
        color: '#4E4C4C',
      },
    },
  },
  boundValueDragIcon: {
    color: '#C4C4C4',
    fontSize: 18,
  },
  alternativesTitle: {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '10px',
    lineHeight: '14px',
    color: '#4c4c4c',
  },
  ...CommonStyles,
});

const DragHandle = sortableHandle(({ classes, editable }) => (
  <div
    className={classes.boundValueDragIconContainer}
  >
    <DragIcon className={`drag-handle-icon ${classes.boundValueDragIcon}`} />
  </div>
));

export default SortableElement(({ item, itemIndex, isNode, nodes, onBoundFieldChange, onBoundRemove }) => {
  const classes = useStyles();

  const altsJoined = item.alt ? item.alt.join('\n') : '';

  const handleKeysWhileInput = (event) => {
    event.stopPropagation();

    if (event.key === 'Escape') {
      onBoundFieldChange('value', itemIndex, item.oldValue);

      onBoundFieldChange('editable', itemIndex, false);
    } else if (event.key === 'Enter') {
      const value = item.value.trim();

      if (value) {
        onBoundFieldChange('oldValue', itemIndex, item.value);
        onBoundFieldChange('editable', itemIndex, false);
      } else {
        onBoundRemove(itemIndex);
      }
    }
  }

  let boundItemRender;

  if (isNode) {
    boundItemRender = (
      <Node nodeId={item.node_item.id}
        isLink
        size="sm"
        nodes={nodes}
        node={nodes[item.node_item]}
      />
    );
  } else if (item.editable) {
    boundItemRender = (
      <React.Fragment>
        <input
          value={item.value}
          onFocus={(e) => {
            onBoundFieldChange('oldValue', itemIndex, item.value);
          }}
          onKeyDown={handleKeysWhileInput}
          className={`bound-input-${itemIndex} ${classes.boundValueInput}`}
          onChange={(e) => onBoundFieldChange('value', itemIndex, e.target.value)}
        />
        
        <h6 className={classes.alternativesTitle} style={{marginLeft: '5px'}}>Alternatives:</h6>

        <TextField
          multiline
          maxRows={100}
          InputProps={{
            disableunderline: 'true',
          }}
          value={altsJoined}
          className={`${classes.textAreaClassName} ${classes.mb10}`}
          onChange={(e) => onBoundFieldChange('alt', itemIndex, e.target.value.split('\n'))}
        />
      </React.Fragment>
    );
  } else {
    boundItemRender = (
      <React.Fragment>
        <p className={classes.boundValueText}>
          {item.value}
        </p>

        {altsJoined && (
          <figure style={{margin: '5px'}}>
            <figcaption className={classes.alternativesTitle}>Alternatives:</figcaption>
            <ul style={{padding: '6.75px 0px'}}>
              {item.alt.map(altString => <li>{altString}</li>)}
            </ul>
          </figure>
        )}
      </React.Fragment>
    );
  }

  let saveEditButton;
  if (!item.editable) {
    saveEditButton = (
      <EditIcon
        className={`bound-icon ${classes.boundValueEditIcon}`}
        onClick={() => {
          onBoundFieldChange('editable', itemIndex, true, () => {
            document.querySelector(`.bound-input-${itemIndex}`).focus();
          });
        }}
      />
    )
  } else {
    saveEditButton = (
      <CheckIcon
        className={classes.boundValueCheckIcon}
        onClick={() => {
          const value = item.value.trim();

          if (value) {
            onBoundFieldChange('oldValue', itemIndex, item.value);
            onBoundFieldChange('editable', itemIndex, false);
          } else {
            onBoundRemove(itemIndex);
          }
        }}
      />
    );
  }

  return (
    <li className={`${classes.boundValue} ${classes.flex}`}>
      <div className={classes.boundValueContainer}>
        <DragHandle classes={classes} editable={item.editable} />

        <div className={classes.boundInputAndControls}>
          <div style={{ flexGrow: 1, minWidth: '400px' }}>{boundItemRender}</div>

          <div className={`${classes.boundEditContainer}`}>
            {!isNode ? saveEditButton : <div style={{ marginLeft: '30px' }} />}
          </div>
        </div>
      </div>
      <div className={`${classes.mr20} ${classes.mt10}`}>
        <ArrowTooltipCss text={'Delete'}>
          <ClearIcon
            onClick={() => onBoundRemove(itemIndex)}
            className={`bound-icon ${classes.boundValueRemoveIcon}`}
          />
        </ArrowTooltipCss>
      </div>
    </li>
  );
});
